import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { ExpenseTable } from "../../components/expenseTable";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit";
import { deleteIncident, updateIncident } from "../../reduxToolkit/incidentsSlice";
import { SubmissionHistory } from "../../components/submissionHistory";
import { RequireIncidentProvided, requireIncident } from "../../wrappers/requireIncident";
import { IncidentSubmitButton } from "../../components/incidentSubmitButton";
import { ExpenseModel, deleteExpense, isCompleteExpense } from "../../reduxToolkit/expensesSlice";
import { present } from "../../../lib/util/present";
import { Tooltip } from "../../components/tooltip";
import { IncidentSelect } from '../../components/formComponents/incidentSelect';
import { chooseSubmissionType } from '../../../lib/rules/chooseSubmissionType';
import { formatDateInTimeZone, parseDateInTimeZone } from '../../../lib/formatDateInTimeZone';
import { useCustomization } from '../../hooks/useCustomizations';
import { endOfYear, startOfDay } from 'date-fns';
import { useConfirm } from '../../providers/confirmDialog';
import { selectFirstTodoReferencingIncident } from '../../reduxToolkit/selectors/selectFirstTodoReferencingIncident';
import { selectAllTodosReferencingIncident } from '../../reduxToolkit/selectors/selectAllTodosReferencingIncident';
import { RealizedTodoModel } from "../../reduxToolkit/todosSlice";
import { useTodoCompletion } from '../../hooks/todos/useTodoCompletion';
import { formatCurrency } from '../../../lib/formatCurrency';
import { isPendingAdvanceRequest, deleteAdvanceRequest } from '../../reduxToolkit/advancesSlice';
import { useFeature } from '../../providers/featureFlags';
import { isSubmitted } from '../../reduxToolkit/submissionsSlice';
import { NewExpenseFormButton } from "../../components/newExpenseFormButton";
import { useNavigateToNewExpense } from "../../hooks/useNavigateToExpense";
import { AdvanceRequestHistory } from "../../components/advanceRequestHistory";
import { deleteAttachment } from "../../reduxToolkit/attachmentsSlice";
import { useTerminology } from "../../hooks/useTerminology";
import { useMinistryQualification } from "../../hooks/useSharingMinistry";
import { selectMinistryContext } from "../../reduxToolkit/selectors/sharingMinistry";
import { advanceRequestSelection } from "./advanceRequest/advanceRequestSelection";

import './show.scss'

interface IncidentShowProps extends RequireIncidentProvided {
}

function IncidentsShow({ incident }: IncidentShowProps) {
  const navigate = useNavigate()
  const navigateToNewExpense = useNavigateToNewExpense()
  const location = useLocation()
  const terms = useTerminology()
  
  const nextTodo = useAppSelector(selectFirstTodoReferencingIncident(incident.id))
  const incidentTodos = useAppSelector(selectAllTodosReferencingIncident(incident.id))

  // Check if there's a 90-day gap TODO for this incident
  const ninetyDayGapTodo = incidentTodos.find((todo) => todo.todo_type === 'ninety-day-gap')

  const incidentHasAdvances = useAppSelector((s) => 
    (s.advances?.advances && s.advances.advances.filter((a) => a.incident_id === incident.id).length > 0) ||
    (s.advances?.advanceRequests && s.advances.advanceRequests.filter((ar) => ar.incident_id === incident.id).length > 0)
  )

  const [highlightExpenseId, setHighlightExpenseId] = useState<string | null>(location.state?.expense_id || null)

  const [showAllExpenses, setShowAllExpenses] = useState(false)
  const pendingExpenses = incident.expenses.filter((e) => !present(e.submitted_at))

  let headerText = <h4>Your Unsubmitted Expenses</h4>
  if (showAllExpenses) {
    headerText = <h4>All Your Expenses</h4>
  } else if (incident.expenses.length === 0) {
    headerText = <i>Add your first expense</i>
  } else if (pendingExpenses.length === 0) {
    headerText = <i>All Expenses Submitted</i>
  }

  let expensestoShow: ExpenseModel[]
  if (showAllExpenses) {
    expensestoShow = incident.expenses
  } else {
    expensestoShow = pendingExpenses
  }
  
  // Once on startup, if there is a highlightExpenseId and it's not in the expensesToShow, set showAllExpenses to true
  useEffect(() => {
    if (highlightExpenseId && !expensestoShow.some((e) => e.id === highlightExpenseId)) {
      setShowAllExpenses(true)
    }
  }, [])

  const isDemoIncident = incident.description === 'Broken Leg (demo incident)'
  const isDisabled = isDemoIncident // In the future, we may have more reasons to disable editing the incident.
  const disabledTooltipText = isDemoIncident ?
    `This is a demo ${terms.incident}, it will be deleted in an hour.` :
    null
    
  return <div className="incidents-show">
    <div className="row incidents-show__header-row">
      <div className="col-12 incidents-show__header">
        <h1>
          {incident.description}
          {incident.closed_at && <span className="badge bg-dark ms-2">Closed</span>}
        </h1>

        <div className="incidents-show__header__actions">
          <div>
            <Tooltip tooltip={disabledTooltipText}>
              <Link to={`/incidents/${incident.id}/edit`}
                  className={`btn btn-sm btn-outline-info me-2 ${isDisabled ? 'disabled' : ''}`}
                  aria-disabled={isDisabled}>
                Edit
              </Link>
            </Tooltip>
            <IncidentCloseButton incident={incident} />
            <IncidentDeleteButton incident={incident} />
          </div>

          <div>
            <AdvanceRequestButton incident={incident} />
          </div>

          {(incident.is_maternity || incident.is_pre_existing_condition) &&
            <div className="incidents-show__header__badges mt-2">
              {incident.is_maternity &&
                <span className="badge bg-info me-2">Maternity {terms.Incident}</span>}
              {incident.is_pre_existing_condition &&
                <span className="badge bg-warning text-dark me-2">Pre-existing Condition</span>}
            </div>}
        </div>
        <div className="incidents-show__header__settings">
          
        </div>
      </div>

      <div className='col-12 col-lg-6 incidents-show__info'>
        <p className=''>
          <strong>Patient:</strong> <br />
          {incident.patient_name}
        </p>
        <p className=''>
          {incident.is_maternity ?
            <>
              <strong>Due Date:</strong> <br />
              {incident.maternity_due_date ?
                formatDateInTimeZone(incident.maternity_due_date, { format: 'MM/dd/yyyy' }) :
                <span className='editable-placeholder'>mm/dd/yyyy</span>}
            </> : <>
              <strong>Start Date:</strong> <br />
              {incident.start_date ?
                formatDateInTimeZone(incident.start_date, { format: 'MM/dd/yyyy' }) :
                <span className='editable-placeholder'>mm/dd/yyyy</span>}
            </>}
        </p>

        <div>
          <label className='form-label me-auto'><strong>Change {terms.Incident}</strong></label>
          <IncidentSelect
            id={`incident-show/${incident.id || ''}`}
            className="form-select"
            value={incident.id}
            blankOption="search all expenses"
            denyNewIncidents
            onSelect={(value) => {
              if (!incident.id && !value) return
              if (incident.id && !value) return navigate(`/expenses`)

              if (value && 'id' in value && value.id !== incident.id) {
                navigate(`/incidents/${value.id}`)
              }
            }}
            />
        </div>
      </div>

      <div className='col-12 col-lg-6 incidents-show__badges'>
        <IncidentTypeBadge incident={incident} />
      </div>
    </div>
    <div className="row">
      {ninetyDayGapTodo && (
        <div className="col-12 mb-3">
          <div className="alert alert-warning d-flex align-items-start">
            <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>warning</i>
            <div>
              <strong>Expense Gap Detected</strong>
              <br />
              {ninetyDayGapTodo.title}. Move one or more expenses to a different {terms.incident} to resolve this issue.
            </div>
          </div>
        </div>
      )}
      <div className="col-12 incidents-show__documents">
        <IncidentSubmitButton key={`incident/${incident.id}/submit-btn`} incident={incident} />
      </div>

      <hr className='hr-thick' />

      <div className="col-12 incidents-show__expenses">
        <div className="incidents-show__expenses-header">
          <div>
            {headerText}

            <button className="btn btn-link" onClick={() => setShowAllExpenses(!showAllExpenses)}>
              {showAllExpenses ?
                'Show Unsubmitted Expenses Only' :
                'Show All Expenses'}
            </button>
          </div>

          <div className="incidents-show__expenses-add">
            {isDisabled ? 
              <Tooltip tooltip={disabledTooltipText}>
                <button className="btn btn-sm btn-info disabled">
                  + Add Expense
                </button>
              </Tooltip> :
              <NewExpenseFormButton
                incidentId={incident.id}
                prefilledData={{
                  patient_name: incident.patient_name,
                  patient_dob: incident.patient_dob,
                  incident_id: incident.id
                }}
                allowNewIncident={false}
                disabledFields={['patient_name', 'patient_dob', 'incident_id']}
                onInserted={navigateToNewExpense} />}
          </div>
        </div>

        <ExpenseTable expenses={expensestoShow}
          displayOptions={{ hideIncident: true, highlightExpenseId }}
          disableEdit={isDisabled} />
          
        {nextTodo && <>
          <hr />
          <i>Your Next Step:</i>
          <NextTodo todo={nextTodo} />
        </>}
      </div>
      
      {incidentHasAdvances && <div className="col-12 incidents-show__advances mt-4">
        <h4>Advances</h4>
        <AdvanceRequestHistory incident={incident} />
      </div>}

      {(incident.is_addon_incident || incident.submissions.filter(isSubmitted).length > 0) && <>
        <div className="col-12 incidents-show__submissions mt-4">
          <h4>Previous Submissions</h4>
          <SubmissionHistory submissions={incident.submissions} />
          {incident.is_addon_incident &&
            <div className='submission-history__row col-12 card'>
              <div className="card-body">
                <div className={`submission-history__row-header`}>
                  <div className="card-title">
                    <Tooltip tooltip='You checked that you had previously submitted some expenses to CHM for this incident, but did not track them here.'>
                      <i>Previously Submitted to CHM before {formatDateInTimeZone(incident.created_at, { format: 'MMM dd' })}</i>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        </>}
    </div>
  </div>
}

export default requireIncident(IncidentsShow)

function IncidentCloseButton({ incident }: RequireIncidentProvided) {
  const dispatch = useAppDispatch()
  const [confirm] = useConfirm()
  const terms = useTerminology()

  if (incident.closed_at) {
    return <button className="btn btn-sm btn-outline-success me-2"
        onClick={() => {
          const now = new Date().toISOString()
          dispatch(updateIncident({
            id: incident.id,
            updated_at: now,
            closed_at: null
          }))
        }}>
      Reopen
    </button>
  }

  return <button className="btn btn-sm btn-outline-secondary me-2"
      onClick={async () => {
        const confirmed = await confirm({
          title: `Close this ${terms.incident}?`,
          message: `Closed ${terms.incidents} won't generate to-do items and won't appear in your open ${terms.incidents} list. You can still find them by searching or viewing all ${terms.incidents}.`,
        })
        if (confirmed) {
          const now = new Date().toISOString()
          dispatch(updateIncident({
            id: incident.id,
            updated_at: now,
            closed_at: now
          }))
        }
      }}>
    Close
  </button>
}

function IncidentDeleteButton({ incident }: RequireIncidentProvided) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const terms = useTerminology()

  // Check for open advances
  const openAdvances = useAppSelector((s) =>
    s.advances?.advances?.filter((a) => a.incident_id === incident.id && !a.repaid_at) || []
  )
  const pendingAdvanceRequests = useAppSelector((s) =>
    s.advances?.advanceRequests?.filter((ar) => ar.incident_id === incident.id && !ar.approved_at && !ar.rejected_at) || []
  )

  // Get all advances and advance requests for this incident
  const allAdvances = useAppSelector((s) =>
    s.advances?.advances?.filter((a) => a.incident_id === incident.id) || []
  )
  const allAdvanceRequests = useAppSelector((s) =>
    s.advances?.advanceRequests?.filter((ar) => ar.incident_id === incident.id) || []
  )

  // Get all attachments for expenses in this incident
  const expenseIds = incident.expenses.map((e) => e.id)
  const allAttachments = useAppSelector((s) =>
    s.attachments.attachments?.filter((a) =>
      a.table_name === 'expenses' && expenseIds.includes(a.record_id)
    ) || []
  )

  if (present(incident.submitted_at)) {
    // Can't delete an incident that was submitted in the past
    return null
  }

  if (openAdvances.length > 0 || pendingAdvanceRequests.length > 0) {
    return <Tooltip
        tooltip={`Can't delete ${terms.incident === 'incident' ? 'an' : 'a'} ${terms.incident} with open advances`}>
      <button className="btn btn-sm btn-outline-secondary disabled">Delete</button>
    </Tooltip>
  }

  const completeExpenses = incident.expenses.filter(isCompleteExpense)

  if (completeExpenses.length > 0) {
    return <Tooltip
        tooltip={`Delete your pending expenses or move them to another ${terms.incident} first`}>
      <button className="btn btn-sm btn-outline-secondary disabled">Delete</button>
    </Tooltip>
  }

  // Either 0 expenses or only incomplete expenses.
  return <button className="btn btn-sm btn-outline-danger"
      onClick={() => {
        if (incident.expenses.length > 0 &&
            !window.confirm(`Delete this ${terms.incident} and its ${incident.expenses.length} expenses?`)) {
          return
        }

        const now = new Date().toISOString()

        // Note: We don't need to delete advances since incidents with advances cannot be deleted
        // Only delete advance requests

        allAdvanceRequests.forEach((advanceRequest) => {
          dispatch(deleteAdvanceRequest({
            id: advanceRequest.id,
            updated_at: now,
            deleted_at: now
          }))
        })

        // Delete all attachments for expenses in this incident
        allAttachments.forEach((attachment) => {
          dispatch(deleteAttachment({
            id: attachment.id,
            updated_at: now,
            deleted_at: now,
          }))
        })

        incident.expenses.forEach((expense) => {
          dispatch(deleteExpense({
            id: expense.id,
            updated_at: now,
            deleted_at: now
          }))
        })

        dispatch(deleteIncident({
          id: incident.id,
          updated_at: now,
          deleted_at: now
        }))

        navigate(-1)
      }}>
    Delete
  </button>
}

function AdvanceRequestButton({ incident }: RequireIncidentProvided) {
  const advanceTrackingEnabled = useFeature('advance_tracking')
  const { ministryName } = useMinistryQualification()
  const advanceRequests = useAppSelector((s) => s.advances.advanceRequests?.filter((ar) => ar.incident_id === incident.id) || [])
  const advances = useAppSelector((s) => s.advances.advances?.filter((a) => a.incident_id === incident.id) || [])

  if (!advanceTrackingEnabled) {
    return null
  }

  const label = advanceRequests.length === 0 && advances.length === 0
    ? 'Request an Advance'
    : 'Request another Advance'

  if (advanceRequests.some(isPendingAdvanceRequest)) {
    return <Tooltip tooltip="You can only have one open advance request at a time.">
        <button className='btn btn-link btn-sm disabled'>{label}</button>
      </Tooltip>
  }

  // Same rule as the advance-request form: submitted expenses are not part of
  // a new advance, and released advances stay excluded.
  const selection = advanceRequestSelection({
    expenses: incident.expenses,
    submissions: incident.submissions,
    alreadyAdvancedExpenseIds: advances.flatMap((advance) => advance.expense_ids).filter(present),
  })

  if (selection.includedExpenseIds.length > 0) {
    return <Link to={`/incidents/${incident.id}/advance-request/new`} className='btn btn-link btn-sm'>{label}</Link>
  }

  const tooltip = selection.submittedExpenseIds.length > 0
    ? `These expenses were already submitted to ${ministryName}, so they aren't part of a new advance.`
    : 'You already have an advance for these expenses.'

  return <Tooltip tooltip={tooltip}>
      <button className='btn btn-link btn-sm disabled'>{label}</button>
    </Tooltip>
}

function IncidentTypeBadge({ incident }: RequireIncidentProvided) {
  const hra = useCustomization('hra')
  const ministry = useAppSelector(selectMinistryContext)
  const { sharingMinistry } = ministry
  const { ministryName: qualifiedMinistryName, qualifyingAmount } = useMinistryQualification()
  const terms = useTerminology()
  const allMembershipSubmissions = useAppSelector((s) => s.submissions.submissions)
  const allMembershipIncidents = useAppSelector((s) => s.incidents.incidents)
  const submissionType = chooseSubmissionType(
    {
      incident,
      incidentExpenses: incident.expenses,
      incidentSubmissions: incident.submissions,
      allMembershipSubmissions,
      allMembershipIncidents,
    },
    {hra, ministry}
  )


  switch(submissionType) {
    case 'CHM':
      return <>
        <div className="d-none d-md-block">
          <p>This {terms.incident} qualifies for sharing through CHM.</p>
        </div>
        <Tooltip tooltip={`This ${terms.incident} qualifies for sharing through CHM.`}>
          <div className='badge bg-info d-md-none'>
            CHM
          </div>
        </Tooltip>
      </>
    case 'CHM-addon':
      return <>
      <div className="d-none d-md-block">
        <p>This {terms.incident} was previously submitted to CHM, so all new expenses will be add-on expenses.</p>
      </div>
      <Tooltip tooltip={`This ${terms.incident} was previously submitted to CHM, so all new expenses will be add-on expenses.`}>
        <div className='badge bg-secondary d-md-none'>
          CHM Add-On
        </div>
      </Tooltip>
      </>
    case 'Sedera':
      return <>
        <div className="d-none d-md-block">
          <p>This {terms.incident} qualifies for sharing through Sedera.</p>
        </div>
        <Tooltip tooltip={`This ${terms.incident} qualifies for sharing through Sedera.`}>
          <div className='badge bg-info d-md-none'>
            Sedera
          </div>
        </Tooltip>
      </>
    case 'SMI':
      return <>
        <div className="d-none d-md-block">
          <p>This {terms.incident} qualifies for sharing through Samaritan Ministries.</p>
        </div>
        <Tooltip tooltip={`This ${terms.incident} qualifies for sharing through Samaritan Ministries.`}>
          <div className='badge bg-info d-md-none'>
            Samaritan
          </div>
        </Tooltip>
      </>
    case 'HRA':
      return <>
      <div className="d-none d-md-block">
        <p>This {terms.incident} does not qualify for sharing because the pre-discounted amount is less than {formatCurrency(qualifyingAmount)}.
          It may be eligible for reimbursement through your HRA.</p>
      </div>
      <Tooltip tooltip={`This ${terms.incident} does not qualify for sharing.  It may be eligible for reimbursement through your HRA.`}>
        <div className='badge bg-secondary d-md-none'>
          Not Eligible
        </div>
      </Tooltip>
    </>
    case null:
      if (incident.expenses.length === 0) {
        return <>
          <div className="d-none d-md-block">
            <p>
              You have not added any expenses to this {terms.incident} yet.
            </p>
          </div>
          <Tooltip tooltip={`You have not added any expenses to this ${terms.incident} yet.`}>
            <div className='badge bg-secondary d-md-none'>
              No Expenses
            </div>
          </Tooltip>
        </>
      }
    
      if (incident.expenses.some((e) => !isCompleteExpense(e))) {
        return <>
          <div className="d-none d-md-block">
            <p>
              Some of your expenses are missing data.  Please complete them before submitting.
            </p>
          </div>
          <Tooltip tooltip="Some of your expenses are missing data.  Please complete them before submitting.">
            <div className='badge bg-secondary d-md-none'>
              Missing Data
            </div>
          </Tooltip>
        </>
      }

      const threshold = qualifyingAmount
      const qualifiesFor = sharingMinistry ? `sharing through ${qualifiedMinistryName}` : 'reimbursement'
      const noBadgeLabel = sharingMinistry ? 'Not Shareable' : 'No Reimbursement'
      let tooltip = `This ${terms.incident} does not qualify for ${qualifiesFor} because the pre-discounted amount is less than ${formatCurrency(threshold)}.`
      if (hra?.providesHraCard) {
        tooltip += '  If all your expenses were paid with the HRA card, there is no need to submit them.'
      }
      return <>
      <div className="d-none d-md-block">
        <p>
          This {terms.incident} does not qualify for {qualifiesFor}.<br/>
          {hra?.providesHraCard && 'If all your expenses were paid with the HRA card, there is no need to submit them.'}
        </p>
      </div>
      <Tooltip tooltip={tooltip}>
        <div className='badge bg-secondary d-md-none'>
          {noBadgeLabel}
        </div>
      </Tooltip>
    </>
    default:
      return null
  }
}

function NextTodo({ todo: t }: { todo: RealizedTodoModel }) {
  const [onClick, _, modal] = useTodoCompletion()
  
  const today = useMemo(() => startOfDay(new Date(Date.now())), [])
  const eoy = useMemo(() => endOfYear(new Date(Date.now())), [])
  
  const dueDate = present(t.due_date) ? parseDateInTimeZone(t.due_date) : null

  const isPast = t.has_due_date && dueDate && dueDate < today
  const showYear = t.has_due_date && dueDate && dueDate > eoy
          
  return <div className='incidents-show-next-todo' onClick={() => onClick(t)}>
    <button className='incidents-show-next-todo__title btn btn-link'>
      {t.title}
    </button>

    {t.has_due_date && <>
        <br className='d-md-none'/>&nbsp;{isPast && t.due_date ?
          <span className='text-danger'><Tooltip tooltip={`This was due on ${formatDateInTimeZone(t.due_date, { format: 'MMMM d, yyyy' })}`}>past due!</Tooltip></span> :
          <span>&nbsp;{
            t.due_date ?
              `before ${formatDateInTimeZone(t.due_date, { format: showYear ? 'MMMM d, yyyy' : 'MMMM d' })}` :
              'ASAP'}
          </span>}
      </>}
      
    {modal}
  </div>
}
